import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import SignerCeremony from './SignerCeremony';
import SignaturePad, { SignaturePadHandle } from './components/SignaturePad';
const API_CONFIG = {
  API_URL: 'https://api.autoelite.io'
};

// Doc-type-specific input shape. A request item whose document_type
// matches one of these keys renders a structured form instead of a
// file upload widget — the client fills in fields like name/phone for
// references or landlord, and on submit we package them as a text
// blob that goes through the existing upload endpoint.
//
// The repeat: true variants render an "Add reference" button so the
// customer can supply multiple entries; the worker side stays
// agnostic — it just receives a single .txt file.
type FormSchemaField = { key: string; label: string; type?: 'text' | 'tel' | 'email' | 'multiline' | 'number'; required?: boolean };
type FormSchema = { fields: FormSchemaField[]; repeat?: boolean; minEntries?: number; entryLabel?: string };

const FORM_SCHEMAS: Record<string, FormSchema> = {
  personal_references: {
    repeat: true,
    minEntries: 2,
    entryLabel: 'Reference',
    fields: [
      { key: 'name', label: 'Full name', required: true },
      { key: 'relationship', label: 'Relationship (friend, family, etc.)' },
      { key: 'phone', label: 'Phone', type: 'tel', required: true },
      { key: 'email', label: 'Email', type: 'email' },
    ],
  },
  professional_references: {
    repeat: true,
    minEntries: 1,
    entryLabel: 'Reference',
    fields: [
      { key: 'name', label: 'Full name', required: true },
      { key: 'company', label: 'Company', required: true },
      { key: 'position', label: 'Position / role' },
      { key: 'phone', label: 'Phone', type: 'tel', required: true },
      { key: 'email', label: 'Email', type: 'email' },
    ],
  },
  landlord_info: {
    fields: [
      { key: 'landlord_name', label: 'Landlord name', required: true },
      { key: 'landlord_phone', label: 'Landlord phone', type: 'tel', required: true },
      { key: 'landlord_email', label: 'Landlord email', type: 'email' },
      { key: 'monthly_rent', label: 'Monthly rent ($)', type: 'number' },
      { key: 'years_at_address', label: 'Years at this address', type: 'number' },
      { key: 'address', label: 'Current address', type: 'multiline' },
    ],
  },
};

const formSchemaFor = (docType: string): FormSchema | null => FORM_SCHEMAS[docType] || null;

interface ClientDocument {
  id: number;
  file_name: string;
  file_size: number;
  document_name: string;
  document_category: string;
  description?: string;
  uploaded_at: string;
  client_visible: boolean;
}

interface DocumentRequestItem {
  id: number;
  document_type: string;
  document_label: string;
  status: string;
  document_id: number | null;
  uploaded_at: string | null;
  review_notes: string | null;
}

interface DocumentRequest {
  id: number;
  requested_documents: string;
  notes: string | null;
  due_date: string | null;
  status: string;
  created_at: string;
  items: DocumentRequestItem[];
}

const DocumentsPortal: React.FC = () => {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [requests, setRequests] = useState<DocumentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [newRequest, setNewRequest] = useState({
    document_type: '',
    description: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadData, setUploadData] = useState({
    document_name: '',
    document_category: 'Other',
    description: '',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState<number | null>(null);
  const [selectedRequestItem, setSelectedRequestItem] = useState<DocumentRequestItem | null>(null);

  // Form-type request item submission. For doc types listed in
  // FORM_SCHEMAS, we render a structured form (e.g. multiple
  // reference entries) instead of a file picker. On submit we
  // serialize the entries to a human-readable .txt and push it
  // through the existing item-upload endpoint.
  const [formDialogItem, setFormDialogItem] = useState<DocumentRequestItem | null>(null);
  const [formEntries, setFormEntries] = useState<Array<Record<string, string>>>([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Release 43: vehicle inspection reports. Loaded alongside docs and
  // surfaced as a "Your vehicle inspections" section on the portal
  // home. Empty for clients with no deals yet — no section renders.
  interface PortalInspection {
    vehicle_id: number;
    year: number | null; make: string | null; model: string | null; trim: string | null;
    vin: string | null; stock_number: string | null;
    has_inspection: boolean;
    order_status: string | null;
    last_update: string | null;
    signed: boolean;
    acknowledged_at: string | null;
    acknowledged_name: string | null;
    summary: { total: number; passed: number; failed: number } | null;
  }
  const [inspections, setInspections] = useState<PortalInspection[]>([]);
  // Release 44: customer acknowledgment dialog state.
  const [ackTarget, setAckTarget] = useState<PortalInspection | null>(null);
  const [ackName, setAckName] = useState('');
  const [ackSubmitting, setAckSubmitting] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  const ackPadRef = React.useRef<SignaturePadHandle | null>(null);

  // Get client info from URL params or localStorage
  const clientId = new URLSearchParams(window.location.search).get('client_id') || 
                   localStorage.getItem('clientId');
  const accessToken = new URLSearchParams(window.location.search).get('token') || 
                      localStorage.getItem('clientAccessToken');

  useEffect(() => {
    if (clientId && accessToken) {
      fetchDocuments();
      fetchRequests();
      fetchInspections();
    }
  }, [clientId, accessToken]);

  const fetchInspections = async () => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/inspections?client_id=${clientId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) return; // non-fatal
      const data = await response.json();
      setInspections(Array.isArray(data?.inspections) ? data.inspections : []);
    } catch { /* non-fatal — inspections section just won't appear */ }
  };

  const submitAcknowledgment = async () => {
    if (!ackTarget) return;
    const png = ackPadRef.current?.exportPng();
    if (!png) { setAckError('Please sign in the box.'); return; }
    if (!ackName.trim()) { setAckError('Please print your name.'); return; }
    setAckError(null);
    setAckSubmitting(true);
    try {
      const res = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/inspections/${ackTarget.vehicle_id}/acknowledge?client_id=${clientId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            signature_data_url: png,
            signature_name: ackName.trim(),
          }),
        },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to acknowledge');
      setAckTarget(null);
      setAckName('');
      ackPadRef.current?.clear();
      await fetchInspections();
    } catch (err: any) {
      setAckError(err?.message || 'Failed to acknowledge');
    } finally {
      setAckSubmitting(false);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/documents?client_id=${clientId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load documents');
      }

      const data = await response.json();
      setDocuments(data.documents || []);

      // Release 3 — apply the dealer's brand to the browser tab the
      // moment we have the dealer payload. Title prefers the explicit
      // dashboard_page_title; otherwise falls back to the dealer name.
      // Favicon is swapped via a dynamic <link rel="icon">.
      const d = data.dealer;
      if (d) {
        const title = d.dashboard_page_title
          || (d.name ? `${d.name} Portal` : 'Client Portal');
        document.title = title;
        if (d.dashboard_favicon_url) {
          let iconLink = document.querySelector(
            'link[rel="icon"], link[rel="shortcut icon"]',
          ) as HTMLLinkElement | null;
          if (!iconLink) {
            iconLink = document.createElement('link');
            iconLink.rel = 'icon';
            document.head.appendChild(iconLink);
          }
          iconLink.setAttribute('href', d.dashboard_favicon_url);
        }
      }
    } catch (err: any) {
      console.error('Error loading documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    if (!clientId || !accessToken) return;

    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/document-requests?client_id=${clientId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load requests');
      }

      const data = await response.json();
      setRequests(data.requests || []);
    } catch (err: any) {
      console.error('Failed to fetch requests:', err);
    }
  };

  const handleDownload = async (doc: ClientDocument) => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/documents/${doc.id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      window.document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(link);
    } catch (err: any) {
      setError(err.message || 'Failed to download document');
    }
  };

  const handleRequestDocument = async () => {
    try {
      const response = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/requests`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            ...newRequest,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      setRequestDialogOpen(false);
      setNewRequest({ document_type: '', description: '' });
      fetchRequests();
    } catch (err: any) {
      setError(err.message || 'Failed to submit request');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      // Auto-fill document name from filename
      if (!uploadData.document_name) {
        setUploadData({ ...uploadData, document_name: file.name });
      }
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('client_id', clientId!);
      formData.append('document_name', uploadData.document_name || uploadFile.name);
      formData.append('document_category', uploadData.document_category);
      if (uploadData.description) {
        formData.append('description', uploadData.description);
      }

      const response = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload document');
      }

      // Success - reset and refresh
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadData({
        document_name: '',
        document_category: 'Other',
        description: '',
      });
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadForRequest = async (item: DocumentRequestItem, file: File) => {
    setUploadingItemId(item.id);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_name', item.document_label);

      const response = await fetch(
        `${API_CONFIG.API_URL}/api/client-portal/document-requests/${item.id}/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload document');
      }

      // Success - refresh requests and documents
      fetchRequests();
      fetchDocuments();
      setSelectedRequestItem(null);
    } catch (err: any) {
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploadingItemId(null);
    }
  };

  const openFormDialog = (item: DocumentRequestItem) => {
    const schema = formSchemaFor(item.document_type);
    if (!schema) return;
    setFormDialogItem(item);
    setFormError(null);
    // Seed with the minimum entry count (1 for non-repeat, minEntries
    // for repeat) so the customer doesn't see a blank dialog.
    const seedCount = schema.repeat ? Math.max(schema.minEntries || 1, 1) : 1;
    setFormEntries(Array.from({ length: seedCount }, () => ({})));
  };

  const updateFormEntry = (entryIdx: number, key: string, value: string) => {
    setFormEntries(prev => prev.map((entry, i) => (i === entryIdx ? { ...entry, [key]: value } : entry)));
  };

  const addFormEntry = () => setFormEntries(prev => [...prev, {}]);
  const removeFormEntry = (idx: number) =>
    setFormEntries(prev => prev.filter((_, i) => i !== idx));

  const submitFormDialog = async () => {
    if (!formDialogItem) return;
    const schema = formSchemaFor(formDialogItem.document_type);
    if (!schema) return;

    // Required-field validation across all entries. We surface the
    // first missing field and bail so the customer can correct it
    // without losing context.
    for (let i = 0; i < formEntries.length; i++) {
      for (const field of schema.fields) {
        if (field.required && !String(formEntries[i][field.key] || '').trim()) {
          const which = schema.repeat ? `${schema.entryLabel || 'Entry'} ${i + 1}: ` : '';
          setFormError(`${which}${field.label} is required`);
          return;
        }
      }
    }

    setFormSubmitting(true);
    setFormError(null);
    try {
      // Build a human-readable text body so the dealer can read the
      // submission in any document viewer. Keeps the worker side
      // identical to file uploads — no schema change required.
      const lines: string[] = [];
      lines.push(formDialogItem.document_label);
      lines.push(`Submitted: ${new Date().toLocaleString()}`);
      lines.push('');
      formEntries.forEach((entry, idx) => {
        if (schema.repeat) {
          lines.push(`--- ${schema.entryLabel || 'Entry'} ${idx + 1} ---`);
        }
        for (const field of schema.fields) {
          const value = String(entry[field.key] || '').trim();
          if (value) lines.push(`${field.label}: ${value}`);
        }
        lines.push('');
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
      const safeLabel = formDialogItem.document_label.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const file = new File([blob], `${safeLabel}-${formDialogItem.id}.txt`, { type: 'text/plain' });

      await handleUploadForRequest(formDialogItem, file);
      setFormDialogItem(null);
      setFormEntries([]);
    } catch (err: any) {
      setFormError(err?.message || 'Failed to submit');
    } finally {
      setFormSubmitting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCategoryColor = (category: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
      'Identification': 'primary',
      'CarFax': 'info',
      'Financial': 'success',
      'Insurance': 'warning',
      'Contracts': 'secondary',
      'Other': 'default',
    };
    return colors[category] || 'default';
  };

  if (!clientId || !accessToken) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          Invalid access. Please use the link provided by your dealer.
        </Alert>
      </Container>
    );
  }

  // Count outstanding items so the hero can call attention when the
  // customer arrived from an email-link asking for documents.
  const pendingItems = requests
    .flatMap(req => req.items)
    .filter(item => item.status !== 'uploaded' && item.status !== 'approved').length;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="md">
        {/* Branded hero — gradient panel with a short, action-oriented
            headline. We hide the generic top-bar action buttons (request
            / upload) because in practice this portal is used to RESPOND
            to a dealer-initiated request, not to start new ones. */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 3,
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 1 }}>
            Document portal
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
            {pendingItems > 0
              ? `${pendingItems} document${pendingItems === 1 ? '' : 's'} requested`
              : 'You\'re all caught up'}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8, mt: 1 }}>
            {pendingItems > 0
              ? 'Upload files or fill out the requested forms below to complete your file.'
              : 'You have no outstanding document requests right now.'}
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Vehicle inspections — surfaces the inspection report for any
            vehicle this client has a deal on. Read-only; opens the
            bilingual HTML report in a new tab using the customer's
            existing portal token (no separate share-token mint). */}
        {inspections.filter(i => i.has_inspection).length > 0 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              🔧 Your vehicle inspections
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Reports from your dealer's pre-delivery inspection of vehicles on your file.
            </Typography>
            <Stack spacing={2}>
              {inspections.filter(i => i.has_inspection).map((insp) => {
                const label = `${insp.year || ''} ${insp.make || ''} ${insp.model || ''}${insp.trim ? ' ' + insp.trim : ''}`.trim();
                const url = `${API_CONFIG.API_URL}/api/client-portal/inspection-report/${insp.vehicle_id}?client_id=${clientId}`;
                const lastUpdate = insp.last_update
                  ? new Date(insp.last_update).toLocaleDateString()
                  : '';
                return (
                  <Card key={insp.vehicle_id}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" fontWeight={600}>{label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {insp.stock_number ? `Stock #${insp.stock_number}` : insp.vin ? `VIN ${insp.vin}` : ''}
                            {lastUpdate ? ` · Updated ${lastUpdate}` : ''}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {insp.signed && (
                              <Chip size="small" color="success" label="Signed off" />
                            )}
                            {insp.acknowledged_at && (
                              <Chip
                                size="small"
                                color="primary"
                                icon={<CheckIcon style={{ fontSize: 14 }} />}
                                label={`You acknowledged ${new Date(insp.acknowledged_at).toLocaleDateString()}`}
                              />
                            )}
                            {insp.order_status && (
                              <Chip size="small" variant="outlined" label={insp.order_status.replace(/_/g, ' ')} />
                            )}
                            {insp.summary && insp.summary.total > 0 && (
                              <Chip
                                size="small"
                                color={insp.summary.failed > 0 ? 'warning' : 'success'}
                                label={`${insp.summary.passed}/${insp.summary.total} passed`}
                              />
                            )}
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View report
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            href={`${url}&lang=fr`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            FR
                          </Button>
                          {!insp.acknowledged_at && (
                            <Button
                              variant="outlined"
                              color="success"
                              onClick={() => { setAckTarget(insp); setAckName(''); setAckError(null); }}
                            >
                              Acknowledge
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Document Requests */}
        {requests.length > 0 && (
          <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              What your dealer needs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tap each item to upload a file or fill out the form. Submitted items are marked complete.
            </Typography>
            
            <Stack spacing={2}>
              {requests.flatMap(req => req.items).map((item) => {
                const isDone = item.status === 'uploaded' || item.status === 'approved';
                const isFormType = !!formSchemaFor(item.document_type);
                const isBusy = uploadingItemId === item.id;
                return (
                  <Card
                    key={item.id}
                    sx={{
                      border: isDone ? '2px solid #4caf50' : '1px solid #e0e0e0',
                      transition: 'border-color 0.2s ease',
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" fontWeight={600}>
                            {item.document_label}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={isDone ? 'Submitted ✓' : isFormType ? 'Needs to be filled out' : 'Needs to be uploaded'}
                              color={isDone ? 'success' : 'warning'}
                              size="small"
                            />
                            {isFormType && !isDone && (
                              <Chip label="Form" variant="outlined" size="small" color="primary" />
                            )}
                          </Box>
                          {item.uploaded_at && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              Submitted on {formatDate(item.uploaded_at)}
                            </Typography>
                          )}
                        </Box>

                        {!isDone && isFormType && (
                          <Button
                            variant="contained"
                            startIcon={isBusy ? <CircularProgress size={20} color="inherit" /> : <EditIcon />}
                            disabled={isBusy}
                            size="large"
                            onClick={() => openFormDialog(item)}
                          >
                            {isBusy ? 'Submitting...' : 'Fill out'}
                          </Button>
                        )}

                        {!isDone && !isFormType && (
                          <Button
                            variant="contained"
                            component="label"
                            startIcon={isBusy ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                            disabled={isBusy}
                            size="large"
                          >
                            {isBusy ? 'Uploading...' : 'Upload'}
                            <input
                              type="file"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleUploadForRequest(item, file);
                              }}
                            />
                          </Button>
                        )}

                        {isDone && (
                          <Chip icon={<CheckIcon />} label="Complete" color="success" size="medium" />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Documents Grid — files the dealer has shared with this
            client (or attached to a vehicle on their file). Scoped
            server-side so other clients' documents never appear. */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? null : (
          <>
            <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 2 }}>
              Documents shared with you
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            {documents.map((doc) => (
              <Box key={doc.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <DocumentIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom noWrap title={doc.document_name}>
                      {doc.document_name}
                    </Typography>
                    <Chip
                      label={doc.document_category}
                      size="small"
                      color={getCategoryColor(doc.document_category)}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {doc.file_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(doc.file_size)} • {formatDate(doc.uploaded_at)}
                    </Typography>
                    {doc.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, fontStyle: 'italic' }}
                      >
                        {doc.description}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(doc)}
                    >
                      Download
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            ))}
            </Box>
          </>
        )}

        {/* Acknowledge inspection dialog (Release 44) */}
        <Dialog
          open={!!ackTarget}
          onClose={() => !ackSubmitting && setAckTarget(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Acknowledge inspection
            {ackTarget && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {`${ackTarget.year || ''} ${ackTarget.make || ''} ${ackTarget.model || ''}`.trim()}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                Sign below to confirm you've reviewed the inspection report.
                Your acknowledgment is saved alongside the technician's sign-off.
              </Typography>
              <SignaturePad
                ref={ackPadRef}
                width={460}
                height={150}
                label="Your signature"
              />
              <TextField
                label="Printed name"
                value={ackName}
                onChange={(e) => setAckName(e.target.value)}
                fullWidth
                size="small"
              />
              {ackError && <Alert severity="error">{ackError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAckTarget(null)} disabled={ackSubmitting}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={submitAcknowledgment}
              disabled={ackSubmitting}
            >
              {ackSubmitting ? 'Submitting…' : 'Submit acknowledgment'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Request Document Dialog */}
        <Dialog
          open={requestDialogOpen}
          onClose={() => setRequestDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Request a Document</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                label="Document Type"
                value={newRequest.document_type}
                onChange={(e) => setNewRequest({ ...newRequest, document_type: e.target.value })}
                fullWidth
                placeholder="e.g., Insurance Certificate, CarFax Report"
              />
              <TextField
                label="Description"
                value={newRequest.description}
                onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
                placeholder="Provide any additional details about the document you need"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleRequestDocument}
              disabled={!newRequest.document_type}
            >
              Submit Request
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload Document Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onClose={() => !uploading && setUploadDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Upload Document</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                fullWidth
              >
                {uploadFile ? uploadFile.name : 'Choose File'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileSelect}
                />
              </Button>

              {uploadFile && (
                <Alert severity="info">
                  File size: {formatFileSize(uploadFile.size)}
                </Alert>
              )}

              <TextField
                label="Document Name"
                value={uploadData.document_name}
                onChange={(e) => setUploadData({ ...uploadData, document_name: e.target.value })}
                fullWidth
                placeholder="e.g., Proof of Income - May 2026"
              />

              <TextField
                select
                label="Category"
                value={uploadData.document_category}
                onChange={(e) => setUploadData({ ...uploadData, document_category: e.target.value })}
                fullWidth
              >
                <option value="Identification">Identification</option>
                <option value="Financial">Financial</option>
                <option value="Insurance">Insurance</option>
                <option value="Contracts">Contracts</option>
                <option value="Other">Other</option>
              </TextField>

              <TextField
                label="Description (Optional)"
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
                placeholder="Add any notes about this document"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadDocument}
              disabled={!uploadFile || !uploadData.document_name || uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Structured-form dialog for request items whose document_type
            has a FORM_SCHEMAS entry (references, landlord info). Renders
            text fields the customer fills in, then serializes to a
            text blob and pipes through the existing item-upload route. */}
        <Dialog
          open={!!formDialogItem}
          onClose={() => !formSubmitting && setFormDialogItem(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{formDialogItem?.document_label}</DialogTitle>
          <DialogContent>
            {(() => {
              const schema = formDialogItem ? formSchemaFor(formDialogItem.document_type) : null;
              if (!schema) return null;
              return (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {formError && (
                    <Alert severity="error" onClose={() => setFormError(null)}>{formError}</Alert>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Please fill in the information below. Your dealer will receive it as soon as you submit.
                  </Typography>

                  {formEntries.map((entry, entryIdx) => (
                    <Paper
                      key={entryIdx}
                      variant="outlined"
                      sx={{ p: 2, position: 'relative' }}
                    >
                      {schema.repeat && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {schema.entryLabel || 'Entry'} {entryIdx + 1}
                          </Typography>
                          {formEntries.length > (schema.minEntries || 1) && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<RemoveIcon />}
                              onClick={() => removeFormEntry(entryIdx)}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      )}
                      <Stack spacing={1.5}>
                        {schema.fields.map(field => (
                          <TextField
                            key={field.key}
                            label={field.label}
                            required={field.required}
                            type={field.type === 'multiline' ? 'text' : field.type || 'text'}
                            multiline={field.type === 'multiline'}
                            rows={field.type === 'multiline' ? 2 : undefined}
                            value={entry[field.key] || ''}
                            onChange={(e) => updateFormEntry(entryIdx, field.key, e.target.value)}
                            fullWidth
                            size="small"
                          />
                        ))}
                      </Stack>
                    </Paper>
                  ))}

                  {schema.repeat && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addFormEntry}
                      variant="outlined"
                      size="small"
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Add another {(schema.entryLabel || 'entry').toLowerCase()}
                    </Button>
                  )}
                </Stack>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormDialogItem(null)} disabled={formSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submitFormDialog}
              disabled={formSubmitting}
              startIcon={formSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            >
              {formSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

// Thin router: e-signature flow at /sign/:token takes over the page,
// otherwise the documents portal renders. Kept hook-free so ESLint's
// rules-of-hooks check is happy and so we don't need react-router for
// a single conditional surface.
const App: React.FC = () => {
  const m = window.location.pathname.match(/^\/sign\/([^/?#]+)/);
  if (m) return <SignerCeremony token={decodeURIComponent(m[1])} />;
  return <DocumentsPortal />;
};

export default App;
